import React from "react";
import { Sparkles, ShieldCheck, Activity, Brain, FileText, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
const getCategoryStyles = (categoryName: string = "") => {
  const lowerCat = categoryName.toLowerCase();
  if (lowerCat.includes("hygiene") || lowerCat.includes("clean")) {
    return {
      icon: Sparkles,
      color: "text-teal-600",
      bg: "bg-teal-50",
      border: "border-teal-100"
    };
  }
  if (lowerCat.includes("procedure") || lowerCat.includes("surgery") || lowerCat.includes("treatment")) {
    return {
      icon: Activity,
      color: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-100"
    };
  }
  if (lowerCat.includes("myth") || lowerCat.includes("fact")) {
    return {
      icon: Brain,
      color: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-100"
    };
  }
  if (lowerCat.includes("prevent") || lowerCat.includes("protect")) {
    return {
      icon: ShieldCheck,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      border: "border-emerald-100"
    };
  }
  return {
    icon: FileText,
    color: "text-slate-600",
    bg: "bg-slate-50",
    border: "border-slate-100"
  };
};
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} className="h-[400px] rounded-xl bg-muted/30 animate-pulse border border-border/50" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground mb-4">Unable to load the archive at this time.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="bg-muted/30 p-4 rounded-full mb-4">
          <FileText className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-serif font-medium mb-2">No articles found</h3>
        <p className="text-muted-foreground">Check back later for new content.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const category = post.categories?.[0];
        const catName = category?.name || "General";
        const styles = getCategoryStyles(catName);
        const Icon = styles.icon;
        return <div key={post.id} data-index={i} className="h-full">
              <Card className="h-full flex flex-col bg-[#FAFAFA] border-[#F1F5F9] hover:shadow-md transition-shadow duration-300 overflow-hidden group">
                <CardHeader className="pb-4 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={`p-2.5 rounded-lg ${styles.bg} ${styles.color} border ${styles.border}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {category && <Badge variant="secondary" className="bg-white border-slate-100 text-slate-600 font-normal tracking-wide">
                        {category.name}
                      </Badge>}
                  </div>
                  <CardTitle className="font-serif text-2xl leading-tight text-foreground group-hover:text-primary/80 transition-colors">
                    <Link to={post.link || "#"}>
                      {post.title}
                    </Link>
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <div className="text-muted-foreground font-sans text-sm leading-relaxed line-clamp-3" dangerouslySetInnerHTML={{
                __html: post.excerpt || ""
              }} />
                </CardContent>

                <CardFooter className="pt-4 border-t border-slate-100/50 mt-auto">
                  <Link to={post.link || "#"} className="w-full">
                    <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent hover:text-primary group-hover:translate-x-1 transition-all duration-300">
                      <span className="font-medium">Read Article</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>;
      })}
      </div>

      {}
      {totalPages > 1 && <div className="flex items-center justify-center gap-4 pt-8 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="w-32">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <span className="text-sm font-medium text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="w-32">
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>}
    </div>;
}
export default function TheFullArchive() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-full-archive" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-50/50 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-24 w-64 h-64 bg-teal-50/30 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="outline" className="border-primary/20 text-primary/80 px-4 py-1 rounded-full">
            Knowledge Base
          </Badge>
          <h2 className="text-4xl md:text-5xl font-serif text-foreground tracking-tight">
            The Full Archive
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            Explore our complete collection of evidence-based dental guides, hygiene tips, and myth-busting articles designed to empower your oral health journey.
          </p>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}